import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/ui/page-header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { Plus, Trash2, Calendar, Edit2, Repeat } from "lucide-react";
import { useRecurringTransactions } from "@/hooks/queries/useRecurringQuery";
import { useAccounts } from "@/hooks/queries/useAccountsQuery";
import {
  useAddRecurring,
  useUpdateRecurring,
  useDeleteRecurring,
  useToggleRecurring,
} from "@/hooks/mutations/useRecurringMutations";
import { parseLocalDate, todayISO } from "@/lib/dates";

const INCOME_CATEGORIES = ["Salary", "Freelance", "Investment", "Gift", "Other"];
const EXPENSE_CATEGORIES = [
  "Housing", "Transportation", "Food", "Utilities", "Healthcare",
  "Entertainment", "Shopping", "Education", "Savings", "Other"
];

interface RecurringTransaction {
  id: string;
  type: "income" | "expense";
  amount: number;
  category: string;
  account: string;
  description: string | null;
  frequency: string;
  next_occurrence: string;
  is_active: boolean;
}

const defaultFormState = () => ({
  type: "expense" as "income" | "expense",
  amount: "",
  category: "",
  account: "",
  description: "",
  frequency: "monthly",
  nextOccurrence: todayISO(),
});

const Recurring = () => {
  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState(defaultFormState());

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RecurringTransaction | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(todayISO());

  const { toast } = useToast();
  const { parseNumber, decimalSeparator, getPlaceholder, formatCurrency } = useUserPreferences();

  // Data
  const { data: recurring = [], isLoading } = useRecurringTransactions();
  const { data: accountList = [] } = useAccounts();
  const accountNames = accountList.map((a) => a.name);

  // Mutations
  const addRecurring = useAddRecurring();
  const updateRecurring = useUpdateRecurring();
  const deleteRecurring = useDeleteRecurring();
  const toggleRecurring = useToggleRecurring();

  const categories = formState.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const resetForm = () => setFormState(defaultFormState());

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const { type, amount, category, account, description, frequency, nextOccurrence } = formState;
    const resolvedAccount = account || accountNames[0];

    addRecurring.mutate(
      {
        type,
        amount: parseNumber(amount),
        category,
        account: resolvedAccount,
        description: description.trim() || null,
        frequency,
        next_occurrence: nextOccurrence,
      },
      {
        onSuccess: () => {
          toast({ title: "Success", description: "Recurring transaction created" });
          resetForm();
          setDialogOpen(false);
        },
        onError: (error: any) => {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        },
      }
    );
  };

  const handleToggle = (id: string, is_active: boolean) => {
    toggleRecurring.mutate(
      { id, is_active },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: is_active ? "Recurring transaction activated" : "Recurring transaction paused",
          });
        },
        onError: (error: any) => {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        },
      }
    );
  };

  const handleDelete = (id: string) => {
    deleteRecurring.mutate(id, {
      onSuccess: () => {
        toast({ title: "Success", description: "Recurring transaction deleted" });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    });
  };

  const openEditDialog = (item: RecurringTransaction) => {
    setEditingItem(item);
    setEditAmount(item.amount.toString());
    setEffectiveDate(item.next_occurrence);
    setEditDialogOpen(true);
  };

  const handleUpdateAmount = () => {
    if (!editingItem) return;

    const newAmount = parseNumber(editAmount);
    if (isNaN(newAmount) || newAmount <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }

    updateRecurring.mutate(
      { id: editingItem.id, amount: newAmount, next_occurrence: effectiveDate },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: `Amount updated to ${formatCurrency(newAmount)} starting from ${parseLocalDate(effectiveDate).toLocaleDateString()}`,
          });
          setEditDialogOpen(false);
          setEditingItem(null);
        },
        onError: (error: any) => {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
      <PageHeader
        icon={Repeat}
        title="Recurring Transactions"
        description="Manage your subscriptions and regular payments"
      >
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Recurring
        </Button>
      </PageHeader>

      <div className="grid gap-4">
        {recurring.map((item) => (
          <Card key={item.id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={item.type === "income" ? "default" : "secondary"} className={item.type === "income" ? "bg-income" : "bg-expense"}>
                      {item.type}
                    </Badge>
                    <span className="font-semibold">{item.category}</span>
                    <Badge variant="outline" className="capitalize">{item.frequency}</Badge>
                    {!item.is_active && <Badge variant="secondary">Paused</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.account}</p>
                  {item.description && (
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    Next: {parseLocalDate(item.next_occurrence).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xl font-bold ${item.type === "income" ? "text-income-text" : "text-expense-text"}`}>
                    {item.type === "income" ? "+" : "-"}{formatCurrency(item.amount)}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(item as RecurringTransaction)} title="Edit amount">
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Switch
                      checked={item.is_active ?? true}
                      onCheckedChange={(checked) => handleToggle(item.id, checked)}
                    />
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {recurring.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              No recurring transactions yet. Add your first recurring transaction!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Recurring Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={formState.type}
                  onValueChange={(v: "income" | "expense") =>
                    setFormState((s) => ({ ...s, type: v, category: "" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formState.amount}
                  onChange={(e) => setFormState((s) => ({ ...s, amount: e.target.value }))}
                  placeholder={getPlaceholder()}
                  required
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">Use "{decimalSeparator}" for decimals</p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formState.category}
                  onValueChange={(v) => setFormState((s) => ({ ...s, category: v }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Account</Label>
                <Select
                  value={formState.account}
                  onValueChange={(v) => setFormState((s) => ({ ...s, account: v }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountNames.map((acc) => (
                      <SelectItem key={acc} value={acc}>{acc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={formState.frequency}
                  onValueChange={(v) => setFormState((s) => ({ ...s, frequency: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Next Occurrence</Label>
                <Input
                  type="date"
                  value={formState.nextOccurrence}
                  onChange={(e) => setFormState((s) => ({ ...s, nextOccurrence: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={formState.description}
                onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))}
                maxLength={500}
                rows={2}
              />
            </div>
            <Button type="submit" disabled={addRecurring.isPending} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Recurring Transaction
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Amount Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Recurring Amount</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant={editingItem.type === "income" ? "default" : "secondary"} className={editingItem.type === "income" ? "bg-income" : "bg-expense"}>
                    {editingItem.type}
                  </Badge>
                  <span className="font-medium">{editingItem.category}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Current amount: {formatCurrency(editingItem.amount)} ({editingItem.frequency})
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-amount">New Amount</Label>
                <Input
                  id="edit-amount"
                  type="text"
                  inputMode="decimal"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder={getPlaceholder()}
                  className="font-mono"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">Use "{decimalSeparator}" for decimals</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="effective-date">Effective From</Label>
                <Input
                  id="effective-date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  The new amount will apply starting from this date
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleUpdateAmount}
                  disabled={updateRecurring.isPending}
                >
                  Update Amount
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recurring;
